/* eslint-disable react/jsx-handler-names */
import React, { useEffect, useMemo, useRef } from 'react'
import { useForm, Controller, FormProvider, FieldErrors } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { getFormData, getLoadingState, IMedicareComplaintAction } from 'redux/reducers/medicare-complaint-form'

import { MyDateField } from 'components/ReactHookForm/MyDateField'
import { PDTContentDrawer, MNTContentDrawer } from 'components/MyDrawer'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { getErrorMessageRegister, getClassNames } from 'utils/react-hook-form'

import { Button } from 'components/Button/Button'
import Cleave from 'cleave.js/react'
import { useTranslation } from 'react-i18next'
import { ClickEventOptions, sendTealiumClickEvent } from 'utils/sendTealiumEvent'
import * as english from 'i18n/en'
import { fieldRuleGenerator } from '../validation/field-rules'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { getModalType, setModal } from 'redux/reducers/modal'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Component } from '@cms-max/medicare-api-sdk'

export interface Step1Props {
  handleOnSubmit: (data: any) => void;
  handleOnBack: (data: any) => void;
  handleOnAltForm: (data: any) => void;
  handleOnError: (data: any) => void;
}

export const Step1: React.FC<Step1Props> = ({ handleOnSubmit, handleOnBack, handleOnAltForm, handleOnError }) => {
  // Modal Logic
  const modalTypeOpen = useSelector(getModalType)
  const dispatch = useDispatch()

  // QM Logic
  const blastMaskingClass = 'qm-block-pii'

  // Form Logic
  const formData = useSelector(getFormData)

  const defaultValuesObject = useMemo(() => {
    return ({
      mbi: formData.mbi,
      coverageStartDate: formData.coverageStartDate,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfBirth: formData.dateOfBirth,
      zipCode: formData.zipCode,
      entType: formData.entType // potentially overridden by resetFormState
    })
  }, [formData])

  const methods = useForm<IMedicareComplaintAction>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultValuesObject,
    shouldFocusError: false
  })
  const { handleSubmit, control, reset, formState, setValue, getValues, watch } = methods
  const { errors } = formState

  const loadingState = useSelector(getLoadingState)

  const { t } = useTranslation('mcfStep1Page')

  useEffect(() => {
    reset(formData, {
      keepErrors: true
    })
  }, [loadingState, formData, reset])

  useEffect(() => {
    reset(defaultValuesObject)
    // setDefaultValuesObject(formData) // Force a rerender if needed, create a useState object for defaultValuesObject
  }, [formData, reset, defaultValuesObject])

  const ref = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  const openModalMedicareNumberTip = () => {
    sendTealiumClickEvent({
      event_action: english.mcfStep1Page['Your Medicare information'],
      event_label: 'CTA: ' + english.mcfStep1Page['Where can I find my Medicare Number?'],
      event_name: 'medicare_complaint_form_ui_clicked',
      text: english.mcfStep1Page['Where can I find my Medicare Number?'],
      header: english.mcfStep1Page['Your Medicare information'],
      event_type: 'ui interaction',
      step_number: '1',
      page: 'Verification Page'
    } as ClickEventOptions)
    if (modalTypeOpen === undefined) {
      dispatch(setModal({ modalType: 'mnt' }))
    } else {
      dispatch(setModal({ modalType: undefined }))
    }
  }

  const openModalPlanDateTip = () => {
    sendTealiumClickEvent({
      event_action: english.mcfStep1Page['Your Medicare information'],
      event_label: `CTA: Where can I find my ${planType()} start date?`,
      event_name: 'medicare_complaint_form_ui_clicked',
      text: `Where can I find my ${planType()} start date?`,
      header: english.mcfStep1Page['Your Medicare information'],
      event_type: 'ui interaction',
      step_number: '1',
      page: 'Verification Page'
    } as ClickEventOptions)

    if (modalTypeOpen === undefined) {
      dispatch(setModal({ modalType: 'pdt' }))
    } else {
      dispatch(setModal({ modalType: undefined }))
    }
  }

  const togglePlanAB = () => {
    sendTealiumClickEvent({
      event_action: english.mcfStep1Page['Your Medicare information'],
      event_label: `CTA: Switch to ${oppositePlanType()}`,
      event_name: 'medicare_complaint_form_ui_clicked',
      text: `Switch to ${oppositePlanType()}`,
      header: english.mcfStep1Page['Your Medicare information'],
      event_type: 'ui interaction',
      step_number: '1',
      page: 'Verification Page'
    } as ClickEventOptions)
    const singleValue = getValues('entType')
    if (singleValue === 'A') {
      setValue('entType', 'B')
    } else {
      setValue('entType', 'A')
    }
  }

  const planType = () => {
    const singleValue = getValues('entType')
    return singleValue === 'A' ? 'Part A' : 'Part B'
  }

  const oppositePlanType = () => {
    const singleValue = getValues('entType')
    return singleValue === 'A' ? 'Part B' : 'Part A'
  }

  const watchPlanType = watch('entType') // Force Rerenders on entType

  const alertRef = useRef<HTMLDivElement | null>(null)
  const handleOnFormError = (data: FieldErrors) => {
    handleOnError(data)
    alertRef.current?.focus() // only works in chrome if user tabs and enters the submit buttom via keyboard
  }

  // If API request fails, focus on errors
  useEffect(() => {
    if (loadingState === 'error') {
      alertRef.current?.focus()
    }
  }, [loadingState])

  return (
    <div>
      {modalTypeOpen === 'mnt' && (
        <MNTContentDrawer onClose={openModalMedicareNumberTip} />
      )}
      {modalTypeOpen === 'pdt' && (
        <PDTContentDrawer onClose={openModalPlanDateTip} />
      )}

      <FormProvider {...methods}>
        <form data-testid='form-step1' onSubmit={handleSubmit(handleOnSubmit, handleOnFormError)}>
          <div className='ds-l-form-row'>
            <div className='ds-l-col--12'>
              <h1 className='ds-text-heading--3xl my-c-main-header-margin' id='maincontent' tabIndex={-1}>{t('Medicare Complaint Form')}</h1>
              <h2 className='ds-text-heading--sm my-u-font-size--18 my-u-mb--6'>{t('Step 1 of 3')}</h2>
              <h2 className='ds-text-heading--2xl my-u-mt--0 my-u-mb--5'>{t('Your Medicare information')}</h2>
              <span className='my-u-mb--6 ds-u-display--block required-text ds-u-font-size--sm'>{t('All fields are required')}</span>
              {/* eslint-disable-next-line operator-linebreak */}
              <div ref={alertRef} tabIndex={-1}>
                {formState.isSubmitted && (loadingState === 'error' || Object.keys(errors).length > 0)
                  ? (
                    <div className='my-u-mb--20 ds-c-alert ds-c-alert--error ds-c-alert--hide-icon' data-testid='step1-alert-error'>
                      <div className='ds-c-alert__body'>
                        <p className='ds-c-alert__text'>
                          {/* api failure: submit error with valid data */}
                          {loadingState === 'error' &&
                            <>
                              <h2 className='ds-c-alert__heading'>{t('step1Error.heading')}</h2>
                              <div>
                                {t('step1Error.text')}
                                <Button
                                  className='my-u-text-align-left ds-c-button ds-c-button--ghost ds-u-padding--0 my-u-font-size--18'
                                  ref={ref}
                                  onClick={handleOnAltForm}
                                  data-testid='step1-alt'
                                >
                                  {t('step1Error.button')}
                                </Button>
                              </div>
                            </>}
                          {/* form inputs error: data is invalid */}
                          {(Object.keys(errors).length > 0) &&
                            <>
                              <h2 className='ds-c-alert__heading'>{t('Error Complete these items to verify your Medicare information')}</h2>
                              <Button
                                className='ds-c-button ds-c-button--ghost  ds-u-padding--0  my-u-font-size--16 ds-u-text-align--left'
                                ref={ref}
                                onClick={handleOnAltForm}
                                data-testid='step1-alt'
                              >
                                {t('alert')}
                              </Button>
                              <ul className=''>
                                {errors.mbi?.message && <li><a href='#mbi'>{t('MEDICARE NUMBER')}</a>{`: ${errors.mbi.message}`}</li>}
                                {errors.coverageStartDate?.month?.message && <li><a href='#coverageStartDate.month'>{t('MONTH')}</a>{`: ${errors.coverageStartDate.month.message}`}</li>}
                                {errors.coverageStartDate?.year?.message && <li><a href='#coverageStartDate.year'>{t('YEAR')}</a>{`: ${errors.coverageStartDate.year.message}`}</li>}
                                {errors.firstName?.message && <li><a href='#firstName'>{t('FIRST NAME')}</a>{`: ${errors.firstName.message}`}</li>}
                                {errors.lastName?.message && <li><a href='#lastName'>{t('LAST NAME')}</a>{`: ${errors.lastName.message}`}</li>}
                                {errors.dateOfBirth?.month?.message && <li><a href='#dateOfBirth.month'>{t('MONTH')}</a>{`: ${errors.dateOfBirth.month.message}`}</li>}
                                {errors.dateOfBirth?.day?.message && <li><a href='#dateOfBirth.day'>{t('DAY')}</a>{`: ${errors.dateOfBirth.day.message}`}</li>}
                                {errors.dateOfBirth?.year?.message && <li><a href='#dateOfBirth.year'>{t('YEAR')}</a>{`: ${errors.dateOfBirth.year.message}`}</li>}
                                {errors.zipCode?.message && <li><a href='#zipCode'>{t('ZIP CODE')}</a>{`: ${errors.zipCode.message}`}</li>}
                              </ul>
                            </>}
                        </p>
                      </div>
                    </div>
                    )
                  : (
                    <>
                      {loadingState !== 'loading' &&
                        <div className='my-u-mb--20 ds-c-alert ds-c-alert--hide-icon' data-testid='step1-alert-info'>
                          <div className='ds-c-alert__body'>
                            <Button
                              className='ds-c-button ds-c-button--ghost  ds-u-padding--0  my-u-font-size--16 ds-u-text-align--left'
                              ref={ref}
                              onClick={handleOnAltForm}
                              data-testid='step1-alt'
                              siteActivityProps={{
                                component: Component.Mcf,
                                clickData: {
                                  linkText: "Don't have your Medicare information available?",
                                  linkUrl: buildAbsoluteMedicareMyUrl('/medicare-complaint/step2b')
                                }
                              }}
                            >
                              {t('alert')}
                            </Button>
                          </div>
                        </div>}
                    </>
                    )
                  }
              </div>
            </div>
          </div>
          <div className='ds-l-form-row my-c-text-field-margin'>
            <Controller
              rules={fieldRuleGenerator.mbi(t)}
              render={({ field, fieldState }) => {
                return (
                  <div className='ds-l-col--auto ds-l-md-col--5' ref={field.ref}>
                    <div>
                      <label className='ds-c-label my-u-font-size--14 preferredNumber' htmlFor='mbi'>{t('MEDICARE NUMBER')}</label>
                      <div>
                        <Button
                          type='button'
                          data-testid='MNTButton'
                          onClick={openModalMedicareNumberTip}
                          className='ds-c-button ds-c-button--ghost my-u-p--0 my-u-mt--2 '
                          siteActivityProps={{
                            component: Component.Mcf,
                            clickData: {
                              linkText: 'Where can I find my Medicare Number?'
                            }
                          }}
                        >
                          {t('Where can I find my Medicare Number?')}
                        </Button>
                      </div>

                    </div>
                    <Cleave
                      data-testid='mbi'
                      id='mbi'
                      options={{ uppercase: true, blocks: [4, 3, 4], delimiter: '-' }}
                      className={`${blastMaskingClass} ch-u-scroll-margin--60 ` + getClassNames(`ds-c-field`, Boolean(fieldState?.error?.message), `ds-c-field--error`)}
                      type='text'
                      name={field.name}
                      onChange={(e) => {
                        field.onChange(e.target.rawValue)
                      }}
                      onBlur={(e) => {
                      // @ts-ignore

                        field.onBlur(e.target.rawValue)
                      }}
                      value={field.value}
                      aria-required
                      aria-invalid={!!fieldState.error}
                    />
                    {getErrorMessageRegister(Boolean(fieldState?.error?.message), fieldState.error?.message ?? '', 'error-message')}
                  </div>
                )
              }}
              name='mbi'
              control={control}
            />
          </div>
          <div className='ds-l-form-row my-c-text-field-margin'>
            <div className='ds-l-col--auto'>
              <MyDateField
                variableInputName='coverageStartDate'
                className={`${blastMaskingClass} ch-u-scroll-margin--120`}
                removeDayField
                fieldErrorStrings={{
                  month: {
                    required: t('incompleteInformation.coverage.month'),
                    invalid: t('incompleteInformation.coverage.month')
                  },
                  day: {
                    required: t('incompleteInformation.coverage.day'),
                    invalid: t('incompleteInformation.coverage.day')
                  },
                  year: {
                    required: t('incompleteInformation.coverage.year'),
                    invalid: t('incompleteInformation.coverage.year')
                  }
                }}
                legendHeader={
                  <legend id='datefield_label_1'>
                    <label htmlFor='coverageStartDate'>{t(planType() + ' Coverage Start Date')}</label>
                    <div>
                      <Button
                        type='button'
                        data-testid='PDTButton'
                        onClick={openModalPlanDateTip}
                        className='ds-c-button ds-c-button--ghost my-u-p--0 my-u-mt--2 '
                        siteActivityProps={{
                          component: Component.Mcf,
                          clickData: {
                            linkText: `Where can I find my ${planType()} start date?`
                          }
                        }}
                      >
                        {t('Where can I find my ' + planType() + ' start date?')}
                      </Button>
                    </div>
                    <span className='ds-c-field__hint ds-u-font-size--base my-u-mt--8'>{t('Use the format MM/YYYY')}</span>
                  </legend>
                }
              />
            </div>
          </div>
          <div className='my-c-text-field-margin'>
            <label htmlFor='coverageStartDate'>{t("Don't have a " + planType() + '?')}</label>
            <div>
              <Button
                type='button'
                data-testid='switch-button'
                onClick={togglePlanAB}
                className='ds-c-button ds-c-button--ghost my-u-p--0 my-u-mt--2 '
                siteActivityProps={{
                  component: Component.Mcf,
                  clickData: {
                    linkText: `Switch to ${watchPlanType && oppositePlanType()}`
                  }
                }}
              >
                {t('Switch to ' + (watchPlanType && oppositePlanType()))}
              </Button>
            </div>
          </div>
          <div className='ds-l-form-row my-c-text-field-margin'>
            <div className='ds-l-col--12 ds-l-md-col--5'>
              <MyTextField
                name='firstName'
                className={blastMaskingClass}
                rules={fieldRuleGenerator.firstName(t)}
                label={t('FIRST NAME')}
                data-testid='firstName'
                maxLength={24}
              />
            </div>
          </div>
          <div className='ds-l-form-row'>
            <div className='ds-l-col--12 ds-l-md-col--5 my-c-text-field-margin'>
              <MyTextField
                name='lastName'
                className={blastMaskingClass}
                rules={fieldRuleGenerator.lastName(t)}
                label={t('LAST NAME')}
                data-testid='lastName'
                maxLength={24}
              />
            </div>
          </div>
          <div className='ds-l-form-row my-c-text-field-margin'>
            <div className='ds-l-col--auto'>
              <MyDateField
                variableInputName='dateOfBirth'
                className={`${blastMaskingClass} ch-u-scroll-margin--90'`}
                fieldErrorStrings={{
                  month: {
                    required: t('incompleteInformation.birthday.month'),
                    invalid: t('incompleteInformation.birthday.month')
                  },
                  day: {
                    required: t('incompleteInformation.birthday.day'),
                    invalid: t('incompleteInformation.birthday.day')
                  },
                  year: {
                    required: t('incompleteInformation.birthday.year'),
                    invalid: t('incompleteInformation.birthday.year')
                  }
                }}
                legendHeader={
                  <legend id='datefield_label_2'>
                    <label htmlFor='dateOfBirth'>{t('DATE OF BIRTH')}</label>
                    <span className='ds-c-field__hint ds-u-font-size--base my-u-mt--8'>{t('Use the format MM/DD/YYYY')}</span>
                  </legend>
                }
              />
            </div>
          </div>
          <div className='ds-l-form-row my-c-text-field-margin'>
            <div className='ds-l-col--auto'>
              <MyTextField
                name='zipCode'
                className={blastMaskingClass}
                rules={fieldRuleGenerator.zipCode(t)}
                label={t('ZIP CODE')}
                data-testid='zipCode'
                maxLength={5}
              />
            </div>
          </div>
          <div className='my-c-text-field-margin'>
            <Button
              data-testid='step1-back'
              ref={ref}
              className='ds-c-button my-u-mr--10 ds-u-margin-y--2'
              onClick={handleOnBack}
              siteActivityProps={{
                component: Component.Mcf,
                clickData: {
                  linkText: 'Cancel',
                  linkUrl: buildAbsoluteMedicareMyUrl('/medicare-complaint')
                }
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              className='ds-c-button ds-c-button--solid'
              data-testid='step1-next'
              type='submit'
              siteActivityProps={{
                component: Component.Mcf,
                clickData: {
                  linkText: 'Next',
                  linkUrl: buildAbsoluteMedicareMyUrl('/medicare-complaint/step2')
                }
              }}
            >
              {loadingState === 'loading' && <MySpinner size='small' inverse isInButton/>}
              {t('Next')}
            </Button>

          </div>
        </form>
      </FormProvider>
    </div>

  )
}
